import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, User, Mail, Calendar, Target, Dumbbell, Flame, Timer, 
  Trophy, Camera, Scale, TrendingUp, TrendingDown, Minus,
  ChevronLeft, ChevronRight, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getImageUrl } from '../../utils/imageUtils';
import { AdminSetPassword } from './AdminSetPassword';
import { getDifficultyLabel } from '../../utils/constants';

const API = process.env.REACT_APP_BACKEND_URL;

const goalLabels = {
  weight_loss: 'Weight Loss',
  muscle_building: 'Muscle Building',
  endurance: 'Endurance',
  general_fitness: 'General Fitness',
  strength: 'Strength',
  core_strength: 'Core Strength'
};

export const ViewUserProfileModal = ({ isOpen, onClose, userId, api }) => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserData();
    }
  }, [isOpen, userId]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const res = await api().get(`/admin/users/${userId}`);
      setUserData(res.data);
    } catch {
      // silent
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const user = userData?.user;
  const progressPictures = userData?.progress_pictures || [];
  const weightHistory = userData?.weight_history || [];

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % progressPictures.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + progressPictures.length) % progressPictures.length);
  };

  // Calculate weight change
  const weightChange = weightHistory.length >= 2 
    ? (weightHistory[0]?.weight - weightHistory[weightHistory.length - 1]?.weight).toFixed(1)
    : 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/90 flex items-start md:items-center justify-center p-0 md:p-4 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-card border border-border w-full max-w-4xl max-h-screen md:max-h-[90vh] overflow-y-auto overflow-x-hidden md:my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border px-3 py-2.5 md:px-4 md:py-3 flex items-center justify-between z-10">
              <h2 className="font-heading text-base md:text-xl text-white uppercase tracking-wider">
                User Profile
              </h2>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                <X className="h-5 w-5" />
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
            ) : !user ? (
              <div className="text-center py-20 text-muted-foreground">
                User not found
              </div>
            ) : (
              <div className="p-3 md:p-6">
                {/* User Header — compact on mobile */}
                <div className="flex items-start gap-3 md:gap-6 mb-4 md:mb-8">
                  <Avatar className="h-14 w-14 md:h-24 md:w-24 border-2 border-primary flex-shrink-0">
                    {user.profile_picture && (
                      <AvatarImage 
                        src={getImageUrl(user.profile_picture)} 
                      />
                    )}
                    <AvatarFallback className="bg-primary/20 text-primary text-lg md:text-2xl">
                      {user.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg md:text-2xl text-white mb-0.5 md:mb-1 truncate">{user.name}</h3>
                    <p className="text-muted-foreground text-xs md:text-base flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-3 truncate">
                      <Mail className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </p>
                    <p className="text-muted-foreground text-[11px] md:hidden mb-1.5">
                      Member since {new Date(user.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex flex-wrap gap-1 md:gap-2 max-w-full">
                      {(user.fitness_goals || []).map(goal => (
                        <Badge key={goal} className="bg-primary/20 text-primary text-[10px] md:text-xs px-1.5 py-0 md:px-2.5 md:py-0.5 whitespace-nowrap">
                          {goalLabels[goal] || goal}
                        </Badge>
                      ))}
                      <Badge className="bg-white/10 text-white capitalize text-[10px] md:text-xs px-1.5 py-0 md:px-2.5 md:py-0.5 whitespace-nowrap">
                        {getDifficultyLabel(user.experience_level) || 'No Level'}
                      </Badge>
                      {user.is_admin && (
                        <Badge className="bg-red-500/20 text-red-400 text-[10px] md:text-xs px-1.5 py-0 md:px-2.5 md:py-0.5 whitespace-nowrap">Admin</Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="hidden md:block text-right">
                    <p className="text-muted-foreground text-sm">Member since</p>
                    <p className="text-white font-medium">
                      {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <AdminSetPassword api={api} userId={userId} userName={user.name} />

                {/* Tabs */}
                <div className="flex gap-1 md:gap-2 mb-3 md:mb-6 border-b border-border pb-2 md:pb-4">
                  {['overview', 'progress', 'photos'].map(tab => (
                    <Button
                      key={tab}
                      variant={activeTab === tab ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setActiveTab(tab)}
                      className={`h-8 px-3 text-xs md:h-9 md:px-4 md:text-sm ${activeTab === tab ? 'bg-primary text-black' : ''}`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </Button>
                  ))}
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-3 md:space-y-6">
                    {/* Stats Grid — tight horizontal cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                      <div className="bg-black border border-border p-2 md:p-4 flex items-center gap-2 md:block">
                        <Dumbbell className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0 md:mb-2" />
                        <div className="min-w-0">
                          <p className="font-heading text-lg md:text-2xl text-white leading-none">{userData.workout_count}</p>
                          <p className="text-muted-foreground text-[11px] md:text-sm leading-tight">Workouts</p>
                        </div>
                      </div>
                      <div className="bg-black border border-border p-2 md:p-4 flex items-center gap-2 md:block">
                        <Timer className="h-5 w-5 md:h-6 md:w-6 text-blue-400 flex-shrink-0 md:mb-2" />
                        <div className="min-w-0">
                          <p className="font-heading text-lg md:text-2xl text-white leading-none">{userData.total_minutes}</p>
                          <p className="text-muted-foreground text-[11px] md:text-sm leading-tight">Minutes</p>
                        </div>
                      </div>
                      <div className="bg-black border border-border p-2 md:p-4 flex items-center gap-2 md:block">
                        <Flame className="h-5 w-5 md:h-6 md:w-6 text-orange-400 flex-shrink-0 md:mb-2" />
                        <div className="min-w-0">
                          <p className="font-heading text-lg md:text-2xl text-white leading-none">{userData.total_calories?.toLocaleString()}</p>
                          <p className="text-muted-foreground text-[11px] md:text-sm leading-tight">Calories</p>
                        </div>
                      </div>
                      <div className="bg-black border border-border p-2 md:p-4 flex items-center gap-2 md:block">
                        <Trophy className="h-5 w-5 md:h-6 md:w-6 text-yellow-400 flex-shrink-0 md:mb-2" />
                        <div className="min-w-0">
                          <p className="font-heading text-lg md:text-2xl text-white leading-none">{userData.current_streak}</p>
                          <p className="text-muted-foreground text-[11px] md:text-sm leading-tight">Day Streak</p>
                        </div>
                      </div>
                    </div>

                    {/* Body Stats */}
                    <div className="bg-black border border-border p-2.5 md:p-6">
                      <h4 className="font-heading text-xs md:text-lg text-white uppercase tracking-wider mb-1.5 md:mb-4">
                        Body Stats
                      </h4>
                      <div className="grid grid-cols-4 gap-1.5 md:gap-6">
                        <div>
                          <p className="text-muted-foreground text-[10px] md:text-sm leading-tight">Age</p>
                          <p className="text-white font-medium text-sm md:text-lg leading-tight">{user.age || '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-[10px] md:text-sm leading-tight">Height</p>
                          <p className="text-white font-medium text-sm md:text-lg leading-tight">{user.height ? `${user.height}"` : '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-[10px] md:text-sm leading-tight">Weight</p>
                          <p className="text-white font-medium text-sm md:text-lg leading-tight">{user.weight ? `${user.weight} lbs` : '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-[10px] md:text-sm leading-tight">Target</p>
                          <p className="text-white font-medium text-sm md:text-lg leading-tight">{user.target_weight ? `${user.target_weight} lbs` : '-'}</p>
                        </div>
                      </div>
                      
                      {user.target_weight && user.weight && user.starting_weight && (
                        <div className="mt-3 md:mt-6">
                          <div className="flex justify-between text-xs md:text-sm mb-1.5 md:mb-2">
                            <span className="text-muted-foreground">Progress to Goal</span>
                            <span className="text-primary">
                              {Math.round(((user.starting_weight - user.weight) / (user.starting_weight - user.target_weight)) * 100)}%
                            </span>
                          </div>
                          <Progress 
                            value={Math.min(100, Math.max(0, ((user.starting_weight - user.weight) / (user.starting_weight - user.target_weight)) * 100))} 
                            className="h-2"
                          />
                        </div>
                      )}
                    </div>

                    {/* Purchases */}
                    {userData.purchases?.length > 0 && (
                      <div className="bg-black border border-border p-3 md:p-6">
                        <h4 className="font-heading text-sm md:text-lg text-white uppercase tracking-wider mb-2 md:mb-4">
                          Purchases ({userData.purchases.length})
                        </h4>
                        <div className="space-y-1.5 md:space-y-2">
                          {userData.purchases.map((purchase) => (
                            <div key={purchase.id || purchase.program_id} className="flex items-center justify-between p-2 md:p-3 bg-white/5">
                              <span className="text-white text-xs md:text-base truncate pr-2">{purchase.program_name || purchase.program_id}</span>
                              <Badge className="bg-green-500/20 text-green-400 text-[10px] md:text-xs flex-shrink-0">Purchased</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Progress Tab */}
                {activeTab === 'progress' && (
                  <div className="space-y-3 md:space-y-6">
                    {/* Weight History */}
                    <div className="bg-black border border-border p-3 md:p-6">
                      <div className="flex items-center justify-between mb-2 md:mb-4">
                        <h4 className="font-heading text-sm md:text-lg text-white uppercase tracking-wider">
                          <Scale className="inline h-4 w-4 md:h-5 md:w-5 mr-1.5 md:mr-2 text-primary" />
                          Weight History
                        </h4>
                        {weightHistory.length >= 2 && (
                          <Badge className={`${parseFloat(weightChange) < 0 ? 'bg-green-500/20 text-green-400' : parseFloat(weightChange) > 0 ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white'}`}>
                            {parseFloat(weightChange) < 0 ? (
                              <TrendingDown className="h-3 w-3 mr-1" />
                            ) : parseFloat(weightChange) > 0 ? (
                              <TrendingUp className="h-3 w-3 mr-1" />
                            ) : (
                              <Minus className="h-3 w-3 mr-1" />
                            )}
                            {Math.abs(weightChange)} lbs
                          </Badge>
                        )}
                      </div>
                      
                      {weightHistory.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {weightHistory.map((entry, idx) => (
                            <div key={entry.id || idx} className="flex items-center justify-between p-3 bg-white/5">
                              <span className="text-muted-foreground">
                                {new Date(entry.date).toLocaleDateString()}
                              </span>
                              <span className="text-white font-medium">{entry.weight} lbs</span>
                              {entry.notes && (
                                <span className="text-muted-foreground text-sm truncate max-w-[200px]">
                                  {entry.notes}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-center py-8">
                          No weight entries recorded
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Photos Tab */}
                {activeTab === 'photos' && (
                  <div className="space-y-3 md:space-y-6">
                    {/* Profile Picture */}
                    <div className="bg-black border border-border p-3 md:p-6">
                      <h4 className="font-heading text-sm md:text-lg text-white uppercase tracking-wider mb-2 md:mb-4">
                        <User className="inline h-4 w-4 md:h-5 md:w-5 mr-1.5 md:mr-2 text-primary" />
                        Profile Picture
                      </h4>
                      {user.profile_picture ? (
                        <div className="flex justify-center">
                          <img 
                            src={getImageUrl(user.profile_picture)}
                            alt="Profile"
                            className="max-w-[300px] max-h-[300px] object-cover border border-border cursor-pointer hover:border-primary transition-colors"
                            onClick={() => {
                              window.open(`${API}${user.profile_picture}`, '_blank');
                            }}
                          />
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-center py-8">
                          No profile picture uploaded
                        </p>
                      )}
                    </div>

                    {/* Progress Pictures */}
                    <div className="bg-black border border-border p-3 md:p-6">
                      <h4 className="font-heading text-sm md:text-lg text-white uppercase tracking-wider mb-2 md:mb-4">
                        <Camera className="inline h-4 w-4 md:h-5 md:w-5 mr-1.5 md:mr-2 text-primary" />
                        Progress Pictures ({progressPictures.length})
                      </h4>
                      
                      {progressPictures.length > 0 ? (
                        <div className="grid grid-cols-3 md:grid-cols-3 md:grid-cols-4 gap-2 md:gap-4">
                          {progressPictures.map((pic, idx) => (
                            <div 
                              key={pic.id}
                              className="relative aspect-square bg-black border border-border overflow-hidden cursor-pointer group"
                              onClick={() => openLightbox(idx)}
                            >
                              <img 
                                src={`${API}${pic.image_url}`}
                                alt={pic.caption || `Progress ${idx + 1}`}
                                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                                <p className="text-white text-sm text-center">
                                  {new Date(pic.created_at).toLocaleDateString()}
                                </p>
                                {pic.weight_at_time && (
                                  <p className="text-primary font-medium">{pic.weight_at_time} lbs</p>
                                )}
                                {pic.caption && (
                                  <p className="text-muted-foreground text-xs mt-1 truncate w-full text-center">
                                    {pic.caption}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-center py-8">
                          No progress pictures uploaded
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Lightbox */}
          <AnimatePresence>
            {lightboxOpen && progressPictures.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
                onClick={() => setLightboxOpen(false)}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4 text-white hover:bg-white/10"
                  onClick={() => setLightboxOpen(false)}
                >
                  <X className="h-6 w-6" />
                </Button>
                
                {progressPictures.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    >
                      <ChevronLeft className="h-8 w-8" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    >
                      <ChevronRight className="h-8 w-8" />
                    </Button>
                  </>
                )}
                
                <div className="max-w-4xl max-h-[80vh] p-4" onClick={(e) => e.stopPropagation()}>
                  <img 
                    src={`${API}${progressPictures[lightboxIndex].image_url}`}
                    alt={progressPictures[lightboxIndex].caption || 'Progress'}
                    className="max-w-full max-h-[70vh] object-contain mx-auto"
                  />
                  <div className="text-center mt-4">
                    <p className="text-white">
                      {new Date(progressPictures[lightboxIndex].created_at).toLocaleDateString()}
                    </p>
                    {progressPictures[lightboxIndex].weight_at_time && (
                      <p className="text-primary font-medium">
                        {progressPictures[lightboxIndex].weight_at_time} lbs
                      </p>
                    )}
                    {progressPictures[lightboxIndex].caption && (
                      <p className="text-muted-foreground mt-2">
                        {progressPictures[lightboxIndex].caption}
                      </p>
                    )}
                    <p className="text-muted-foreground text-sm mt-2">
                      {lightboxIndex + 1} of {progressPictures.length}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
